/**
 * HIPAA F1/F2 session helpers.
 * JWT lives in an HttpOnly cookie (set by the backend) and optionally in
 * process memory for same-tab Bearer compatibility. Never persist JWT in
 * localStorage / sessionStorage / window.name.
 */
import axios from "axios";

const MEMORY_KEYS = new Set(["user", "userAdmin"]);
const PROFILE_KEYS = [
  "userid",
  "userrole",
  "userName",
  "userEmail",
  "useridAdmin",
  "userroleAdmin",
  "userNameAdmin",
];

let memoryTokens = {
  user: null,
  userAdmin: null,
};

export function getMemoryToken(key = "user") {
  return memoryTokens[key] || "";
}

export function setMemoryToken(key, token) {
  if (MEMORY_KEYS.has(key)) {
    memoryTokens[key] = token || null;
  }
}

export function clearMemoryTokens() {
  memoryTokens = { user: null, userAdmin: null };
}

/** Install localStorage shim so legacy Bearer reads don't persist JWT to disk. */
export function installTokenStorageShim() {
  // Drop any previously persisted JWTs from older builds.
  try {
    localStorage.removeItem("user");
    localStorage.removeItem("userAdmin");
    localStorage.removeItem("tokensendbird");
  } catch (_) {
    /* ignore */
  }

  // Never stash tokens in window.name (survives cross-origin navigation).
  try {
    window.name = "";
  } catch (_) {
    /* ignore */
  }

  const proto = Storage.prototype;
  const origGet = proto.getItem;
  const origSet = proto.setItem;
  const origRemove = proto.removeItem;
  const origClear = proto.clear;

  proto.getItem = function (key) {
    if (MEMORY_KEYS.has(key)) {
      // Return empty string (not null) so legacy `.replace()` calls don't throw.
      return memoryTokens[key] || "";
    }
    return origGet.call(this, key);
  };

  proto.setItem = function (key, value) {
    if (MEMORY_KEYS.has(key)) {
      memoryTokens[key] = value == null || value === "" ? null : String(value);
      return;
    }
    return origSet.call(this, key, value);
  };

  proto.removeItem = function (key) {
    if (MEMORY_KEYS.has(key)) {
      memoryTokens[key] = null;
      return;
    }
    return origRemove.call(this, key);
  };

  proto.clear = function () {
    clearMemoryTokens();
    return origClear.call(this);
  };
}

let authInterceptorInstalled = false;

export function configureAxios() {
  axios.defaults.withCredentials = true;
  axios.interceptors.request.use((config) => {
    config.withCredentials = true;
    const auth = config.headers?.Authorization || config.headers?.authorization;
    if (
      typeof auth === "string" &&
      (/^Bearer\s*$/i.test(auth) ||
        /^Bearer\s+(null|undefined)$/i.test(auth))
    ) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
    return config;
  });

  // HIPAA F11: fail closed on expired/invalid session — clear client state and go to login.
  if (!authInterceptorInstalled) {
    authInterceptorInstalled = true;
    axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err?.response?.status;
        const url = String(err?.config?.url || "");
        const isAuthEndpoint =
          /\/admin\/login(\/|$|\?)|\/admin\/login-users|\/admin\/session|\/admin\/logout/i.test(
            url
          );
        if ((status === 401 || status === 403) && !isAuthEndpoint) {
          clearProfile();
          try {
            const path = window.location?.pathname || "";
            if (!/login/i.test(path)) {
              window.location.assign("/login");
            }
          } catch (_) {
            /* ignore */
          }
        }
        return Promise.reject(err);
      }
    );
  }
}

export function setProfile(profile, { admin = false } = {}) {
  if (!profile) return;
  if (admin) {
    if (profile.uuid) localStorage.setItem("useridAdmin", profile.uuid);
    if (profile.role) localStorage.setItem("userroleAdmin", profile.role);
    if (profile.firstname) {
      localStorage.setItem("userNameAdmin", "Hi " + profile.firstname);
    }
  } else {
    if (profile.uuid) localStorage.setItem("userid", profile.uuid);
    if (profile.role) localStorage.setItem("userrole", profile.role);
    if (profile.firstname) {
      localStorage.setItem("userName", "Hi " + profile.firstname);
    }
    if (profile.email) localStorage.setItem("userEmail", profile.email);
  }
}

export function clearProfile() {
  PROFILE_KEYS.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch (_) {
      /* ignore */
    }
  });
  clearMemoryTokens();
}

export async function fetchSession(baseUrl) {
  const url = `${baseUrl}/admin/session`;
  const res = await axios.get(url, { withCredentials: true });
  return res.data;
}

export async function logout(baseUrl) {
  try {
    await axios.post(`${baseUrl}/admin/logout`, {}, { withCredentials: true });
  } catch (_) {
    /* ignore */
  }
  clearProfile();
}
